import { useState } from 'react';
import { Supplier, categoryConfig, statusConfig } from '../../../mocks/suppliers';

interface SupplierDetailModalProps {
  supplier: Supplier;
  onClose: () => void;
  onEdit: () => void;
}

type DetailTab = 'overview' | 'rates' | 'bookings' | 'notes';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i key={s} className={`text-sm ${s <= Math.round(rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-200'}`} />
      ))}
    </div>
  );
}

const bookingStatusConfig = {
  Confirmed: 'bg-sky-50 text-sky-600',
  Completed: 'bg-emerald-50 text-emerald-600',
  Cancelled: 'bg-red-50 text-red-500',
  Pending: 'bg-amber-50 text-amber-600',
};

export default function SupplierDetailModal({ supplier, onClose, onEdit }: SupplierDetailModalProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const cat = categoryConfig[supplier.category];
  const st = statusConfig[supplier.status];

  const margin = supplier.totalSellValue > 0
    ? Math.round(((supplier.totalSellValue - supplier.totalCostValue) / supplier.totalSellValue) * 100)
    : 0;

  const tabs: { id: DetailTab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: 'ri-user-line' },
    { id: 'rates', label: 'Rates', icon: 'ri-price-tag-3-line' },
    { id: 'bookings', label: 'Booking History', icon: 'ri-calendar-check-line' },
    { id: 'notes', label: 'Notes', icon: 'ri-sticky-note-line' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 px-6 py-5 border-b border-gray-100">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-base flex-shrink-0"
            style={{ backgroundColor: supplier.logoColor }}
          >
            {supplier.logo}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-gray-800">{supplier.name}</h2>
              <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.bg} ${st.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                {supplier.status}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cat.bg} ${cat.color}`}>
                <i className={`${cat.icon} mr-1`} />{supplier.category}
              </span>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-map-pin-line text-gray-300" />{supplier.city}, {supplier.country}
              </span>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-global-line text-gray-300" />{supplier.website}
              </span>
              <div className="flex items-center gap-1.5">
                <StarRating rating={supplier.rating} />
                <span className="text-xs font-semibold text-gray-600">{supplier.rating} ({supplier.reviewCount} reviews)</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-edit-line" /> Edit
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-5 divide-x divide-gray-100 border-b border-gray-100">
          {[
            { label: 'Total Bookings', value: supplier.totalBookings.toString(), icon: 'ri-calendar-check-line', color: 'text-amber-600' },
            { label: 'Total Revenue', value: `AED ${supplier.totalSellValue.toLocaleString()}`, icon: 'ri-money-dollar-circle-line', color: 'text-teal-600' },
            { label: 'Gross Margin', value: `${margin}%`, icon: 'ri-percent-line', color: 'text-emerald-600' },
            { label: 'Currency', value: supplier.currency, icon: 'ri-exchange-dollar-line', color: 'text-sky-600' },
            { label: 'Contract Expiry', value: new Date(supplier.contractExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }), icon: 'ri-contract-line', color: 'text-violet-600' },
          ].map((k) => (
            <div key={k.label} className="px-4 py-3 text-center">
              <p className={`text-base font-bold ${k.color}`}>{k.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-6 pt-4 border-b border-gray-100">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === t.id
                  ? 'text-amber-600 border-amber-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              <i className={`${t.icon} text-base`} /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-5">
              {/* About */}
              <div className="col-span-2 space-y-5">
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">About</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{supplier.description}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Contacts</h4>
                  <div className="space-y-3">
                    {supplier.contacts.map((contact, i) => (
                      <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                          style={{ backgroundColor: supplier.logoColor }}
                        >
                          {contact.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">{contact.name}</p>
                          <p className="text-xs text-gray-500 mb-2">{contact.role}</p>
                          <div className="flex flex-wrap gap-3 text-xs">
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-amber-600 hover:underline cursor-pointer">
                              <i className="ri-mail-line" /> {contact.email}
                            </a>
                            <span className="flex items-center gap-1 text-gray-500">
                              <i className="ri-phone-line text-gray-400" /> {contact.phone}
                            </span>
                            {contact.whatsapp && (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <i className="ri-whatsapp-line" /> {contact.whatsapp}
                              </span>
                            )}
                          </div>
                        </div>
                        {i === 0 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Primary</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {supplier.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Panel */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Contract Info</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Terms</span>
                      <span className="font-medium text-gray-700 text-right text-xs">{supplier.paymentTerms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Currency</span>
                      <span className="font-medium text-gray-700">{supplier.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Supplier Since</span>
                      <span className="font-medium text-gray-700">
                        {new Date(supplier.createdAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Contract Expiry</span>
                      <span className="font-medium text-gray-700">
                        {new Date(supplier.contractExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last Used</span>
                      <span className="font-medium text-gray-700">
                        {new Date(supplier.lastUsed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Financial Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Cost</span>
                      <span className="font-semibold text-gray-700">AED {supplier.totalCostValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Revenue</span>
                      <span className="font-semibold text-teal-600">AED {supplier.totalSellValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-2">
                      <span className="text-gray-500 font-semibold">Gross Profit</span>
                      <span className="font-bold text-emerald-600">AED {(supplier.totalSellValue - supplier.totalCostValue).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Margin</span>
                      <span className="font-bold text-emerald-600">{margin}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rates */}
          {activeTab === 'rates' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{supplier.rates.length} rate{supplier.rates.length !== 1 ? 's' : ''} on file</p>
              </div>
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border-b border-gray-100">
                  <div className="col-span-4">Service</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-2 text-right">Cost Price</div>
                  <div className="col-span-1 text-center">Markup</div>
                  <div className="col-span-2 text-right">Sell Price</div>
                  <div className="col-span-1 text-right">Season</div>
                </div>
                {supplier.rates.map((rate, i) => (
                  <div
                    key={rate.id}
                    className={`grid grid-cols-12 px-4 py-3.5 text-sm items-center ${i !== supplier.rates.length - 1 ? 'border-b border-gray-50' : ''}`}
                  >
                    <div className="col-span-4 font-medium text-gray-800">{rate.service}</div>
                    <div className="col-span-2 text-gray-500 text-xs">{rate.unit}</div>
                    <div className="col-span-2 text-right text-gray-600">{rate.currency} {rate.costPrice.toLocaleString()}</div>
                    <div className="col-span-1 text-center">
                      <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-medium">{rate.markupPct}%</span>
                    </div>
                    <div className="col-span-2 text-right font-semibold text-gray-800">{rate.currency} {rate.sellPrice.toLocaleString()}</div>
                    <div className="col-span-1 text-right">
                      <span className="text-xs text-gray-400">{rate.season.split(' ')[0]}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2.5 text-xs text-amber-700">
                <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0" />
                <span>All rates are NET and exclude taxes/fees. Rates valid {new Date(supplier.rates[0]?.validFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} – {new Date(supplier.rates[0]?.validTo).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.</span>
              </div>
            </div>
          )}

          {/* Booking History */}
          {activeTab === 'bookings' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">{supplier.bookings.length} bookings on record (showing recent)</p>
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-500 border-b border-gray-100">
                  <div className="col-span-2">Booking ID</div>
                  <div className="col-span-2">Customer</div>
                  <div className="col-span-3">Service</div>
                  <div className="col-span-1 text-center">Pax</div>
                  <div className="col-span-1 text-center">Date</div>
                  <div className="col-span-1 text-right">Cost</div>
                  <div className="col-span-1 text-right">Revenue</div>
                  <div className="col-span-1 text-right">Status</div>
                </div>
                {supplier.bookings.map((bk, i) => (
                  <div
                    key={bk.bookingId}
                    className={`grid grid-cols-12 px-4 py-3 text-sm items-center ${i !== supplier.bookings.length - 1 ? 'border-b border-gray-50' : ''}`}
                  >
                    <div className="col-span-2 font-mono text-xs text-amber-600 font-semibold">{bk.bookingId}</div>
                    <div className="col-span-2 text-gray-700 text-xs font-medium">{bk.customerName.split(' ')[0]}</div>
                    <div className="col-span-3 text-gray-600 text-xs line-clamp-1">{bk.service}</div>
                    <div className="col-span-1 text-center text-gray-600 text-xs">{bk.pax}</div>
                    <div className="col-span-1 text-center text-xs text-gray-500">
                      {new Date(bk.travelDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </div>
                    <div className="col-span-1 text-right text-xs text-gray-600">
                      {bk.costAmount > 0 ? `${bk.currency} ${bk.costAmount.toLocaleString()}` : '—'}
                    </div>
                    <div className="col-span-1 text-right text-xs font-semibold text-teal-600">
                      {bk.sellAmount > 0 ? `${bk.currency} ${bk.sellAmount.toLocaleString()}` : '—'}
                    </div>
                    <div className="col-span-1 text-right">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${bookingStatusConfig[bk.status]}`}>{bk.status}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">All time: {supplier.totalBookings} bookings</span>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">Total Cost: <span className="font-semibold">AED {supplier.totalCostValue.toLocaleString()}</span></span>
                  <span className="text-teal-600">Total Revenue: <span className="font-semibold">AED {supplier.totalSellValue.toLocaleString()}</span></span>
                  <span className="text-emerald-600">Profit: <span className="font-bold">AED {(supplier.totalSellValue - supplier.totalCostValue).toLocaleString()}</span></span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-sticky-note-line text-amber-500" />
                  <h4 className="text-sm font-semibold text-amber-800">Supplier Notes</h4>
                </div>
                <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-line">{supplier.notes}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Supplier Reference</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Supplier ID</span>
                    <span className="font-mono font-semibold text-gray-700">{supplier.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Category</span>
                    <span className="font-medium text-gray-700">{supplier.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Added</span>
                    <span className="text-gray-700">
                      {new Date(supplier.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`font-semibold ${statusConfig[supplier.status].color}`}>{supplier.status}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
